import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import {
  getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey,
  getListCarrierRequestsApiV1CarrierRequestsGetQueryKey,
} from '#/shared/api/generated/shipment/theCargoShipment'
import {
  WS_MESSAGE_TYPE,
  useWsReconnect,
  useWsSubscription,
  useWsSubscriptions,
} from '#/shared/lib/ws'
import type { WsEnvelope } from '#/shared/lib/ws'

import { invalidateShipmentLists } from './invalidate-shipment-queries'

// Uniform-envelope meta carried by every shipment-domain event. The flat
// `shipment`/`extra` fields are ignored here — we refetch rather than patch.
interface ShipmentEvent {
  entity_id?: string | null
  reload?: string | null
}

// The unified `notification` frame that now backs new leads and shipment
// milestones. We only care about the ones pointing at a shipment.
interface ShipmentNotificationEvent {
  type?: string | null
  entity_type?: string | null
  entity_id?: string | null
}

const SHIPMENT_ENTITY_TYPE = 'shipment'

// Every shipment-domain ws type. Each fires whenever the assignee's (or, for an
// unassigned shipment, the whole org's) shipment changes stage/status/fields, is
// dispatched/posted, or gains a carrier request. All refresh the same list and
// counts queries; carrier-request events (`carrier_request_created`,
// `sd_webhook_event`) additionally refresh the request list. Status milestones
// (order/picked_up/delivered/contract) also arrive as a
// separate `notification` message — that path owns the toast, so this hook only
// invalidates and never toasts (no double-notify).
const SHIPMENT_EVENT_TYPES = [
  WS_MESSAGE_TYPE.shipmentLeadCreated,
  WS_MESSAGE_TYPE.shipmentQuoteCreated,
  WS_MESSAGE_TYPE.shipmentOrderCreated,
  WS_MESSAGE_TYPE.shipmentLeadConverted,
  WS_MESSAGE_TYPE.shipmentLeadAttached,
  WS_MESSAGE_TYPE.shipmentUpdated,
  WS_MESSAGE_TYPE.shipmentOrderCanceled,
  WS_MESSAGE_TYPE.shipmentContractSigned,
  WS_MESSAGE_TYPE.carrierRequestCreated,
  WS_MESSAGE_TYPE.shipmentDispatched,
  WS_MESSAGE_TYPE.shipmentDirectDispatched,
  WS_MESSAGE_TYPE.shipmentDispatchCancelled,
  WS_MESSAGE_TYPE.shipmentPosted,
  WS_MESSAGE_TYPE.shipmentUnposted,
  WS_MESSAGE_TYPE.shipmentPickedUp,
  WS_MESSAGE_TYPE.shipmentDelivered,
  WS_MESSAGE_TYPE.sdWebhookEvent,
] as const

// Event types that mutate carrier requests (creation, plus Central Dispatch
// webhook events like offer.declined/accepted). Receiving any of them refreshes
// the carrier-request list so an open Carrier Requests dialog stays current.
const CARRIER_REQUEST_EVENT_TYPES: readonly string[] = [
  WS_MESSAGE_TYPE.carrierRequestCreated,
  WS_MESSAGE_TYPE.sdWebhookEvent,
]

/**
 * Live-refresh shipment views from socket events. Mounted once in the dashboard
 * shell so any open leads/quotes/orders board or shipment detail stays current
 * when a teammate (or the backend) changes a shipment, dispatches it, or a
 * carrier requests the load — without a manual refresh. Refetch (not in-place
 * patch) keeps it simple and correct; volume is low (events reach only the
 * assignee, else org-broadcast). Reconnecting re-syncs missed events.
 */
export function useShipmentRealtime(): void {
  const queryClient = useQueryClient()

  const refreshLists = useCallback(() => {
    invalidateShipmentLists(queryClient)
  }, [queryClient])

  const onShipmentEvent = useCallback(
    (envelope: WsEnvelope<ShipmentEvent>) => {
      refreshLists()
      const data = envelope.data
      if (data?.entity_id) {
        void queryClient.invalidateQueries({
          queryKey: getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(
            data.entity_id,
          ),
        })
      }
      if (CARRIER_REQUEST_EVENT_TYPES.includes(envelope.type)) {
        void queryClient.invalidateQueries({
          queryKey: getListCarrierRequestsApiV1CarrierRequestsGetQueryKey(),
        })
      }
    },
    [queryClient, refreshLists],
  )

  // A new lead (and every other shipment notification, e.g. order booked/
  // canceled) now arrives only as a `notification` frame — no dedicated
  // `shipment_*` event. Refresh the same lists + counts so the sidebar badges
  // and boards stay live. The notifications widget owns the toast/sound.
  const onNotification = useCallback(
    (envelope: WsEnvelope<ShipmentNotificationEvent>) => {
      const data = envelope.data
      if (data?.entity_type !== SHIPMENT_ENTITY_TYPE) return
      refreshLists()
      if (data.entity_id) {
        void queryClient.invalidateQueries({
          queryKey: getGetShipmentApiV1ShipmentsShipmentIdGetQueryKey(
            data.entity_id,
          ),
        })
      }
    },
    [queryClient, refreshLists],
  )

  useWsSubscriptions<ShipmentEvent>(SHIPMENT_EVENT_TYPES, onShipmentEvent)
  useWsSubscription<ShipmentNotificationEvent>(
    WS_MESSAGE_TYPE.notification,
    onNotification,
  )
  useWsReconnect(refreshLists)
}
